import React from 'react';
import {
	List,
	Datagrid,
	TextField,
	DateField,
	FunctionField,
	useRefresh,
	useNotify,
	useRecordContext,
} from 'react-admin';
import { Button, Chip, Stack, Tooltip } from '@mui/material';

import { approveSignupRequest, rejectSignupRequest } from '../../Services/partners';

// Demandes d'accès à l'espace partenaire, en attente d'arbitrage.
//
// ⚠️ Seules les demandes dont l'EMAIL A ÉTÉ VÉRIFIÉ remontent ici — le filtre est posé
// côté api-partners, pas ici. Un formulaire non vérifié n'engage personne : rien n'empêche
// d'y saisir l'adresse d'un tiers, et l'approbation crée une invitation owner sur cette
// adresse.
//
// Les deux issues ne sont pas symétriques :
//   • Approuver  → crée l'organisation + son propriétaire, et supprime la demande.
//   • Rejeter    → supprime la demande, sans rien créer ni conserver.

// #region StatusChip — ce que l'approbation va produire
// Un compte Quanti existant donne une appartenance ACTIVE immédiatement ; sinon le membre
// est posé par email et reste une invitation en attente, que la personne accepte à sa
// première connexion. L'admin doit le savoir avant de cliquer : dans le second cas, rien
// ne se passe tant que l'intéressé ne s'est pas connecté.
const StatusChip: React.FC = () => {
	const record = useRecordContext();
	if (!record) return null;
	return record.hasQuantiAccount ? (
		<Tooltip title="Un compte Quanti existe déjà : l'accès sera actif immédiatement.">
			<Chip size="small" color="success" label="Compte existant" />
		</Tooltip>
	) : (
		<Tooltip title="Aucun compte Quanti : une invitation sera posée sur cette adresse, à accepter à la première connexion.">
			<Chip size="small" variant="outlined" label="Sur invitation" />
		</Tooltip>
	);
};
// #endregion

// #region DecisionButtons
const DecisionButtons: React.FC = () => {
	const record = useRecordContext();
	const refresh = useRefresh();
	const notify = useNotify();
	const [busy, setBusy] = React.useState(false);

	if (!record) return null;

	const approve = async () => {
		setBusy(true);
		try {
			const partner = await approveSignupRequest(record.id as number);
			notify(`Organisation « ${partner.name} » créée`, { type: 'success' });
			refresh();
		} catch (e: any) {
			notify(e?.message || "Approbation impossible", { type: 'error' });
			setBusy(false);
		}
	};

	// Confirmation explicite : le rejet SUPPRIME la demande, il n'y a pas de corbeille
	// pour la retrouver, et le demandeur n'est pas prévenu.
	const reject = async () => {
		if (!window.confirm(`Rejeter et supprimer la demande de ${record.email} ? Cette action est définitive.`)) return;
		setBusy(true);
		try {
			await rejectSignupRequest(record.id as number);
			notify('Demande rejetée', { type: 'info' });
			refresh();
		} catch (e: any) {
			notify(e?.message || 'Rejet impossible', { type: 'error' });
			setBusy(false);
		}
	};

	return (
		<Stack direction="row" spacing={1}>
			<Button size="small" variant="contained" disabled={busy} onClick={approve}>
				Approuver
			</Button>
			<Button size="small" color="error" disabled={busy} onClick={reject}>
				Rejeter
			</Button>
		</Stack>
	);
};
// #endregion

// #region SignupRequestList
export const SignupRequestList: React.FC = (props: any) => (
	<List {...props} pagination={false} exporter={false} title="Demandes d'accès partenaire">
		<Datagrid bulkActionButtons={false}>
			<TextField source="companyName" label="Société" />
			<FunctionField label="Demandeur" render={(r: any) => `${r?.firstName || ''} ${r?.lastName || ''}`.trim() || '—'} />
			<TextField source="email" label="Email" />
			<TextField source="phone" label="Téléphone" />
			<TextField source="country" label="Pays" />
			<DateField source="emailVerifiedAt" label="Email confirmé le" showTime />
			<FunctionField label="À la validation" render={() => <StatusChip />} />
			<FunctionField label="" render={() => <DecisionButtons />} />
		</Datagrid>
	</List>
);
// #endregion
